'use client';

import React, { useState, useCallback, useEffect } from 'react'
import Select from 'react-select'

import { APIError, useAxios } from '../core/_axios';
import { Ipad } from '../core/_models';
import LoadingSpinner from './LoadingSpinner';
import useServices from '../hooks/useServices';
import { sendServiceToIpadByUniqueIdentifier } from '../core/_requests_v2';
import useLocalizedCopy from '../hooks/useLocalizedCopy';

export default function ModalSendServiceToIpad({
    active: modalIsOpen,
    setActive: setModalIsOpen,
    iPad,
    setIpad
}: {
    active: boolean,
    setActive: (active: boolean) => void,
    iPad: Ipad,
    setIpad: (ipad: Ipad) => void
}) {
    const axios = useAxios();
    const { copy } = useLocalizedCopy();

    const {
        services,
        loadingServices,
        errorLoadingServices,
        loadAllServices,
    } = useServices();

    const [sendingService, setSendingService] = useState(false);
    const [errorSendingService, setErrorSendingService] = useState<string | null>(null);

    const onFormSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (sendingService) {
            return;
        }
        setErrorSendingService(null);
        setSendingService(true);
        const form = event.currentTarget;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        try {
            const resultingiPad = await sendServiceToIpadByUniqueIdentifier(axios, iPad.unique_identifier, data.service_unique_id as string);
            setIpad(resultingiPad);
            setModalIsOpen(false);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : copy.Common.messages.errorOccurred
            setErrorSendingService(errorMessage)
            setSendingService(false)
        }
    }, [axios, iPad.unique_identifier, sendingService, setIpad, setModalIsOpen]);

    useEffect(() => {
        if (!modalIsOpen) {
            setErrorSendingService(null);
            setSendingService(false);
        } else {
            loadAllServices();
        }
    }, [loadAllServices, modalIsOpen]);

    return (
        <div>
            <>
                {modalIsOpen ? (
                    <>
                        <div
                            className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none text-left"
                        >
                            {(loadingServices || errorLoadingServices) ? <>
                                <div className="relative w-auto my-6 mx-auto max-w-3xl">
                                    {/*content*/}
                                    <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                                        {/*header*/}
                                        <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
                                            <h3 className="text-xl font-semibold">
                                                {copy.IPads.manage.sendService}
                                            </h3>
                                        </div>
                                        {/*body*/}
                                        <div className="relative p-6 flex-auto">
                                            <div className="max-h-[50vh] min-w-[600px] overflow-y-auto overflow-x-hidden">
                                                {errorLoadingServices ? <div>
                                                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                                                        <span className="block sm:inline">
                                                            {errorLoadingServices}
                                                        </span>
                                                    </div>
                                                    <div className="mt-5">
                                                        <button className="btn btn-lg btn-primary me-2" onClick={loadAllServices}>
                                                            {copy.Layout.retry}
                                                        </button>
                                                        <button className="btn btn-lg btn-light" onClick={() => {
                                                            setModalIsOpen(false)
                                                        }}>
                                                            {copy.Common.buttons.cancel}
                                                        </button>
                                                    </div>
                                                </div> : <LoadingSpinner />}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </> : <>
                                <form className="relative w-auto my-6 mx-auto max-w-3xl" onSubmit={onFormSubmit}>
                                    {/*content*/}
                                    <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                                        {/*header*/}
                                        <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
                                            <h3 className="text-xl font-semibold">
                                                {copy.IPads.manage.sendService}
                                            </h3>
                                        </div>
                                        {/*body*/}
                                        <div className="relative p-6 flex-auto">
                                            <div className="max-h-[50vh] min-w-[600px] overflow-y-auto overflow-x-hidden">
                                                <div className="grid gap-7.5">
                                                    <div className="flex flex-col gap-4">
                                                        <div>
                                                            <label
                                                                htmlFor="ipad_name"
                                                                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                                                {copy.Common.labels.select} {copy.Common.navigation.services} ({copy.Common.labels.required})
                                                            </label>
                                                            <div className='px-4' style={{ height: 200 }}>
                                                                <Select
                                                                    name="service_unique_id"
                                                                    required
                                                                    options={services.map((service) => ({
                                                                        value: service.unique_identifier,
                                                                        label: service.public_data.deceased_person.full_name
                                                                    }))}
                                                                />
                                                            </div>
                                                            {false && <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                                                e.g. narthex, back chapel, viewing room, etc.
                                                            </div>}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {/*footer*/}
                                        <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
                                            <div className='flex-grow'>
                                                {errorSendingService && <div className="text-red-500 text-sm font-medium">{errorSendingService}</div>}
                                            </div>
                                            <button
                                                className="btn btn-sm btn-light me-2"
                                                type="button"
                                                onClick={() => {
                                                    setModalIsOpen(false)
                                                }}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="btn btn-sm btn-primary"
                                                type="submit"
                                                disabled={sendingService}
                                            >
                                                {!sendingService ? copy.IPads.manage.sendService : copy.Common.labels.processing + '...'}
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </>}
                        </div>
                        <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
                    </>
                ) : null}
            </>
        </div>
    )
}